// src/pages/DataProcessingAgreement.js
// Data Processing Agreement Page

import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../components/common';
import './Upgrade.css';

function DataProcessingAgreement() {

  return (
    <div className="upgrade-container">
      <SEOHead
        title="Data Processing Agreement | DriftAlarm"
        description="DriftAlarm Data Processing Agreement (DPA) for GDPR compliance. Details sub-processors, data security, retention, breach notification, and data subject rights."
        path="/dpa"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Data Processing Agreement — DriftAlarm",
            "url": "https://www.driftalarm.com/dpa",
            "dateModified": "2026-03-13T00:00:00-05:00",
            "publisher": {
              "@type": "Organization",
              "name": "DriftAlarm",
              "url": "https://www.driftalarm.com",
              "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" },
              "foundingDate": "2026",
              "sameAs": ["https://www.driftalarm.com"]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
              { "@type": "ListItem", "position": 2, "name": "Data Processing Agreement" }
            ]
          }
        ]}
      />
      <PublicHeader activeLink={null} />

      {/* Main Content */}
      <div className="upgrade-content legal-content">
        <h1>Data Processing Agreement</h1>
        <p className="legal-updated">Last Updated: March 2026</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            This Data Processing Agreement ("DPA") supplements the Terms of Service between you
            ("Customer", "Controller") and DriftAlarm ("Processor").
          </p>
          <p>
            This DPA governs the processing of personal data by DriftAlarm in connection with the Service.
          </p>
          <p>
            This DPA is effective for the duration of the service agreement and applies to customers
            who require formal data processing documentation.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Definitions</h2>
          <ul>
            <li>
              <strong>Controller:</strong> The entity that determines the purposes and means of
              processing (the Customer — you decide which assets to scan).
            </li>
            <li>
              <strong>Processor:</strong> The entity that processes data on behalf of the Controller
              (DriftAlarm).
            </li>
            <li>
              <strong>Data Subject:</strong> An identifiable natural person whose data is processed.
            </li>
            <li>
              <strong>Personal Data:</strong> Any information relating to an identified or identifiable
              natural person.
            </li>
            <li>
              <strong>Processing:</strong> Any operation performed on personal data (collection, storage,
              analysis, deletion).
            </li>
            <li>
              <strong>Sub-processor:</strong> A third party engaged by the Processor to process personal data.
            </li>
            <li>
              <strong>Supervisory Authority:</strong> An independent public authority responsible for data
              protection oversight.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Scope of Processing</h2>

          <h3>3a. Data DriftAlarm Processes</h3>
          <ul>
            <li>Domain names and IP addresses submitted for scanning</li>
            <li>Scan results: discovered vulnerabilities, open ports, SSL certificates, DNS records, subdomain lists</li>
            <li>Technology stack data: web servers, frameworks, CMS platforms detected</li>
            <li>Email addresses (for account management and notifications)</li>
            <li>User account data: display name, organization, subscription tier, usage counters</li>
            <li>API key metadata (names, scopes, usage statistics — NOT the key itself)</li>
            <li>Drift detection data: baseline snapshots, change events, alarm configurations</li>
            <li>Notification configurations: email addresses, Slack webhook URLs, generic webhook URLs</li>
          </ul>

          <h3>3b. Data DriftAlarm Does NOT Process</h3>
          <ul>
            <li>Payment card data — subscription billing is handled by our payment processor, Stripe (a sub-processor); DriftAlarm does not store full card numbers</li>
            <li>Health data (HIPAA not in scope)</li>
            <li>Special category personal data (racial/ethnic origin, political opinions, religious beliefs, biometric data)</li>
            <li>Employee data beyond what's in scan results (e.g., email addresses found in public records)</li>
          </ul>

          <h3>3c. Processing Purposes</h3>
          <ul>
            <li>External attack surface management and continuous monitoring</li>
            <li>Vulnerability assessment and security analysis</li>
            <li>Configuration drift detection and alerting</li>
            <li>AI-powered remediation guidance and risk scoring</li>
            <li>Report generation (weekly, monthly, executive)</li>
            <li>Service operation, maintenance, and improvement</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Roles and Responsibilities</h2>

          <h3>Customer (Controller)</h3>
          <ul>
            <li>Determines which assets to scan</li>
            <li>Responsible for authorization to scan targets</li>
            <li>Configures notification channels and alarm rules</li>
            <li>Manages user access within their organization</li>
          </ul>

          <h3>DriftAlarm (Processor)</h3>
          <ul>
            <li>Processes scan data on behalf of Customer</li>
            <li>Implements technical and organizational security measures</li>
            <li>Assists with data subject requests when applicable</li>
            <li>Notifies Controller of data breaches</li>
            <li>Maintains records of processing activities</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Sub-processors</h2>
          <p>DriftAlarm uses the following sub-processors:</p>
          <ul>
            <li>
              <strong>Microsoft Azure</strong> — Cloud hosting, storage, authentication. Data: All
              platform data. Location: East US (United States).
            </li>
            <li>
              <strong>Anthropic</strong> — AI-powered security analysis. Data: Scan findings, technology
              data. Location: United States.
            </li>
            <li>
              <strong>GreyNoise</strong> — IP reputation classification. Data: IP addresses. Location:
              United States.
            </li>
            <li>
              <strong>ProjectDiscovery</strong> — Nuclei vulnerability template updates. Data: No customer
              data — template downloads only. Location: United States.
            </li>
            <li>
              <strong>Microsoft Graph API</strong> — Email delivery. Data: Email addresses, notification
              content. Location: United States.
            </li>
          </ul>
          <p>
            DriftAlarm will notify Customer before engaging new sub-processors.
          </p>
          <p>
            Customer may object to new sub-processors within 30 days.
          </p>
          <p>
            If Customer objects and DriftAlarm cannot provide the Service without the sub-processor,
            either party may terminate.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Data Security Measures</h2>

          <h3>6a. Technical Measures</h3>
          <ul>
            <li>Encryption at rest: Azure Storage Service Encryption (AES-256)</li>
            <li>Encryption in transit: HTTPS/TLS 1.2+ for all connections</li>
            <li>Authentication: Azure AD External ID (CIAM) with JWT token validation</li>
            <li>API key security: SHA-256 hashed storage, key prefix only retained</li>
            <li>Per-user data partitioning: all scan data isolated by user ID (partition key)</li>
            <li>Scan type isolation: separate result tracking per scan type</li>
            <li>Network security: Azure platform network controls</li>
          </ul>

          <h3>6b. Organizational Measures</h3>
          <ul>
            <li>Access limited to authorized personnel</li>
            <li>Incident response procedures</li>
            <li>Data retention policies enforced via Azure Blob lifecycle rules</li>
            <li>Regular platform security assessments</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Data Retention</h2>
          <ul>
            <li>
              <strong>Scan result blobs:</strong> 90 days (30 days active → 30 days Cool tier → deletion
              via Azure Blob lifecycle).
            </li>
            <li>
              <strong>Asset findings:</strong> Retained while asset is active. Deleted across all resources
              on asset removal.
            </li>
            <li>
              <strong>Drift baselines and events:</strong> Retained while asset is active. Deleted with
              asset removal.
            </li>
            <li>
              <strong>GreyNoise enrichment cache:</strong> 24 hours. Automatic TTL expiry.
            </li>
            <li>
              <strong>AI remediation cache:</strong> 7 days. Automatic TTL expiry.
            </li>
            <li>
              <strong>User account data:</strong> Retained while account is active. Deleted within 90 days
              of account closure.
            </li>
          </ul>
          <p>
            On service termination: all customer data deleted within 90 days per retention policy.
          </p>
          <p>
            Customer may request earlier deletion by contacting{' '}
            <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Data Breach Notification</h2>
          <p>
            DriftAlarm will notify the Controller without undue delay and within 72 hours of becoming
            aware of a confirmed personal data breach.
          </p>
          <p>Notification will include:</p>
          <ul>
            <li>Nature of the breach (what happened)</li>
            <li>Categories and approximate number of data subjects affected</li>
            <li>Categories and approximate number of records affected</li>
            <li>Likely consequences of the breach</li>
            <li>Measures taken or proposed to address the breach and mitigate adverse effects</li>
            <li>Contact point for further information (<a href="mailto:support@driftalarm.com">support@driftalarm.com</a>)</li>
          </ul>
          <p>
            DriftAlarm will cooperate with the Controller's breach investigation.
          </p>
          <p>
            DriftAlarm will document all data breaches, including facts, effects, and remedial actions.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Data Subject Rights</h2>
          <p>
            DriftAlarm will assist the Controller in responding to data subject requests for:
          </p>
          <ul>
            <li>Access to personal data</li>
            <li>Rectification of inaccurate data</li>
            <li>Erasure of personal data ("right to be forgotten")</li>
            <li>Restriction of processing</li>
            <li>Data portability (export in machine-readable format — CSV/JSON available)</li>
            <li>Objection to processing</li>
          </ul>
          <p>
            Process: Contact <a href="mailto:support@driftalarm.com">support@driftalarm.com</a> with the request.
          </p>
          <p>
            Response time: DriftAlarm will respond to Controller requests within 30 days.
          </p>
          <p>
            Customer is responsible for verifying the identity of data subjects before forwarding requests.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Audits and Compliance</h2>
          <p>
            DriftAlarm will make available to the Controller information necessary to demonstrate
            compliance with data processing obligations.
          </p>
          <p>
            DriftAlarm will allow for and contribute to audits conducted by the Controller or an
            authorized auditor, with reasonable notice.
          </p>
          <p>
            Audits shall be conducted during business hours and shall not unreasonably interfere
            with DriftAlarm's operations.
          </p>
          <p>
            Controller shall bear the cost of audits.
          </p>
          <p>
            Note: DriftAlarm does not currently maintain SOC 2 certification — this is planned for
            a future milestone.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. International Data Transfers</h2>
          <p>
            All data is processed in Microsoft Azure East US region (United States).
          </p>
          <p>
            For EU/EEA customers: Standard Contractual Clauses (SCCs) are available on request to
            provide adequate safeguards for international data transfers.
          </p>
          <p>
            DriftAlarm will comply with applicable data transfer mechanisms as required by supervisory
            authorities.
          </p>
          <p>
            Contact <a href="mailto:support@driftalarm.com">support@driftalarm.com</a> for SCC documentation.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Term and Termination</h2>
          <p>
            This DPA is effective for the duration of the Service agreement between Controller and Processor.
          </p>
          <p>
            This DPA automatically terminates when the Service agreement terminates.
          </p>
          <p>Upon termination:</p>
          <ul>
            <li>DriftAlarm will cease processing personal data within 30 days</li>
            <li>All customer data will be deleted within 90 days per the retention policy</li>
            <li>DriftAlarm will provide written confirmation of deletion upon request</li>
          </ul>
          <p>
            Obligations that survive termination: confidentiality, cooperation with audits for data
            processed during the term.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Liability</h2>
          <p>
            DriftAlarm's liability under this DPA is subject to the limitation of liability provisions
            in the Terms of Service.
          </p>
          <p>
            Each party is liable for damages caused by processing that infringes applicable data
            protection law.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Contact</h2>
          <p>
            All inquiries: <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Related Policies</h2>
          <ul>
            <li><Link to="/terms">Terms of Service</Link></li>
            <li><Link to="/privacy">Privacy Policy</Link></li>
            <li><Link to="/acceptable-use">Acceptable Use Policy</Link></li>
          </ul>
        </section>
      </div>

      <PublicFooter />
    </div>
  );
}

export default DataProcessingAgreement;
